/**
 * useGPSTracking Hook
 * 
 * Subscribe to GPS tracking channel and broadcast rider location
 */

import { useEffect, useRef } from 'react';
import { wsClient, WS_CHANNELS } from '@/lib/ws';
import { useRiderStore } from '@/store/rider';
import { useOrdersStore } from '@/store/orders';
import { haversineDistance } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface GPSUpdate {
  riderId: string;
  lat: number;
  lng: number;
  accuracy: number;
  speed?: number;
  heading?: number;
  timestamp: string;
}

// RouteUpdate interface removed - not currently used
// Can be added back when route updates are implemented

const GPS_UPDATE_INTERVAL = 5000; // 5 seconds
const MIN_DISTANCE_THRESHOLD = 10; // 10 meters (Haversine filter)

export function useGPSTracking() {
  const riderStatus = useRiderStore((state) => state.status);
  const currentLocation = useRiderStore((state) => state.currentLocation);
  const setCurrentLocation = useRiderStore((state) => state.setCurrentLocation);
  const activeOrder = useOrdersStore((state) => state.activeOrder);
  
  const lastBroadcastLocation = useRef<{ lat: number; lng: number } | null>(null);
  const watchId = useRef<number | null>(null);
  const broadcastInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only track GPS when rider is available or busy
    if (riderStatus === 'offline') {
      stopTracking();
      return;
    }

    // Start GPS tracking
    startTracking();

    // DISABLED: WebSocket not available (Socket.IO vs native WS incompatibility)
    logger.warn('[useGPSTracking] WebSocket route updates disabled');

    return () => {
      stopTracking();
    };
  }, [riderStatus, activeOrder]);

  const startTracking = () => {
    if (!navigator.geolocation) {
      logger.error('Geolocation not supported', new Error('Geolocation API not available'));
      return;
    }

    logger.info('Starting GPS tracking');

    // Watch position
    watchId.current = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          bearing: position.coords.heading ?? undefined, // Heading/direction in degrees
        };

        // Update local state
        setCurrentLocation(newLocation);

        // Check if we should broadcast (Haversine filter)
        if (shouldBroadcast(newLocation)) {
          broadcastLocation(newLocation, position.coords.speed, position.coords.heading);
          lastBroadcastLocation.current = { lat: newLocation.lat, lng: newLocation.lng };
        }
      },
      (error) => {
        logger.error('Geolocation error', new Error(error.message));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );

    // Setup periodic broadcast (backup to watchPosition)
    broadcastInterval.current = setInterval(() => {
      if (currentLocation) {
        broadcastLocation(currentLocation, undefined, undefined);
      }
    }, GPS_UPDATE_INTERVAL);
  };

  const stopTracking = () => {
    if (watchId.current !== null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }

    if (broadcastInterval.current) {
      clearInterval(broadcastInterval.current);
      broadcastInterval.current = null;
    }

    logger.info('Stopped GPS tracking');
  };

  const shouldBroadcast = (newLocation: { lat: number; lng: number }): boolean => {
    if (!lastBroadcastLocation.current) {
      return true; // First broadcast
    }

    // Calculate distance from last broadcast
    const distance = haversineDistance(
      lastBroadcastLocation.current.lat,
      lastBroadcastLocation.current.lng,
      newLocation.lat,
      newLocation.lng
    );

    // Only broadcast if moved more than threshold
    return distance >= MIN_DISTANCE_THRESHOLD;
  };

  const broadcastLocation = (
    location: { lat: number; lng: number; accuracy: number },
    speed?: number | null,
    heading?: number | null
  ) => {
    if (!wsClient.isConnected()) {
      logger.warn('Cannot broadcast location - WebSocket not connected');
      return;
    }

    const update: GPSUpdate = {
      riderId: 'current', // Will be set by server from auth token
      lat: location.lat,
      lng: location.lng,
      accuracy: location.accuracy,
      speed: speed ?? undefined,
      heading: heading ?? undefined,
      timestamp: new Date().toISOString(),
    };

    wsClient.send(WS_CHANNELS.GPS_TRACKING, 'update', update);
    logger.debug('GPS location broadcasted', {
      lat: update.lat.toString(),
      lng: update.lng.toString(),
      accuracy: update.accuracy.toString(),
    });
  };

  return {
    isTracking: riderStatus !== 'offline',
    currentLocation,
  };
}

